export default function HostPilotPrivacy() {
  return (
    <main className="min-h-screen bg-[#071118] text-slate-300">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-emerald-400">
            Privacy Policy - HostPilot Web Server
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            Effective date: <time dateTime="2026-07-21">July 21, 2026</time>
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed">
          <p>
            <strong className="text-slate-100">Cosmovex</strong> built{" "}
            <strong className="text-slate-100">HostPilot</strong>, an app that
            runs a local web server on your Android device, as an ad-supported
            application with an optional Pro upgrade. This Privacy Policy
            explains what information HostPilot may process, how that
            information is used, and the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong className="text-slate-100">Files you serve:</strong>{" "}
              sites and files you add to HostPilot are stored locally in the
              app&apos;s own folder and are served directly from your device.
              They are never uploaded to Cosmovex or any cloud service.
            </li>
            <li>
              <strong className="text-slate-100">
                Local network availability:
              </strong>{" "}
              when you start a server with LAN access enabled, the files in the
              selected site become reachable by other devices that can connect
              to your device&apos;s network address and port. You control when
              the server runs, which site is served, and whether access is
              limited to your device only (loopback mode) or protected with a
              username and password (Pro).
            </li>
            <li>
              <strong className="text-slate-100">Access logs:</strong> HostPilot
              keeps a recent log of requests handled by your server (time,
              requested path, response code, and the connecting device&apos;s
              local address) so you can see activity. Logs stay on your device
              and are never transmitted.
            </li>
            <li>
              <strong className="text-slate-100">App preferences:</strong>{" "}
              theme, language, and server settings (port, access mode) are
              stored locally on your device.
            </li>
            <li>
              <strong className="text-slate-100">Usage and ad data:</strong> if
              ads are enabled, AdMob or related Google Mobile Ads services may
              collect device and advertising data as described in Google&apos;s
              own privacy documentation.
            </li>
            <li>
              <strong className="text-slate-100">
                Analytics and diagnostics:
              </strong>{" "}
              Firebase Analytics and Firebase Crashlytics may collect app
              stability, usage patterns, and crash reports to improve
              reliability and user experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-emerald-400">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To run a web server on your device and serve your files to
              devices you choose to share them with on your local network.
            </li>
            <li>
              To keep the server running reliably in the background with a
              persistent status notification.
            </li>
            <li>To show you live server status, uptime, and request activity.</li>
            <li>To remember your sites, settings, and preferences.</li>
            <li>To improve app performance, reliability, and product quality.</li>
            <li>
              To display ads in supported versions of the app, where applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-emerald-400">Local Storage</h2>
          <p>
            HostPilot is designed to keep your data on your device. Sites,
            files, access logs, and preferences are stored locally and are not
            uploaded to any server operated by us. Data remains on your device
            until you remove it, clear the app&apos;s storage, or uninstall the
            app.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            Serving Files on Your Network
          </h2>
          <p>
            Running a server means devices on the same network can request the
            files in the site you are serving while the server is on. HostPilot
            never opens your files to the wider internet by itself — it binds
            only to your device&apos;s local addresses. Please choose what you
            serve carefully, use loopback mode when you only need access from
            your own device, and use the Pro password protection feature when
            sharing anything sensitive on a shared network.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            Data You Receive From Other People
          </h2>
          <p>
            Some HostPilot endpoints are designed to <em>receive</em> data
            rather than serve it, so people other than you may send information
            to your device:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-300">
            <li>
              <strong>File Drop</strong> — anyone with your link can upload
              files, which are saved into that endpoint&apos;s{" "}
              <code>collected</code> folder on your device.
            </li>
            <li>
              <strong>Request Inspector</strong> — captures the requests sent to
              it, including headers, form fields, request bodies and the
              sender&apos;s IP address, so you can inspect them.
            </li>
            <li>
              <strong>Inbox</strong> — records what arrived, when, and the IP
              address it came from, and can show a notification.
            </li>
          </ul>
          <p>
            All of this is stored only on your device and is never sent to us.
            Because it can include other people&apos;s personal information, you
            are responsible for what you collect and for handling it lawfully —
            share these links only with people you intend to receive data from,
            use the Pro password protection on shared networks, and delete what
            you no longer need. Deleting an item from the Inbox, or deleting the
            endpoint, removes it from your device.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            PHP and Databases
          </h2>
          <p>
            HostPilot can run PHP code and SQLite databases on your device, and
            includes a browser-based database console. The PHP runtime, your
            code, and your databases all run locally — nothing is compiled,
            executed or stored on any server of ours, and we never receive your
            database contents. Code you run has the same access to that
            endpoint&apos;s folder as the app itself, so only run PHP you trust.
            If you use the built-in client to connect to an external MySQL or
            MariaDB server, that connection is made directly from your device to
            the server you name, and its privacy terms are between you and that
            provider.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">Network Access</h2>
          <p>
            The server itself works entirely on your local network and does not
            need internet access. The app makes internet requests only for ads,
            Firebase analytics and crash reporting, remote configuration, and
            the rotating cross-promotion banner on the home screen. No served
            file bytes or access-log data are ever transmitted to us.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">Advertising</h2>
          <p>
            HostPilot may use <strong className="text-slate-100">Google AdMob</strong>{" "}
            to show banner, interstitial, and app-open ad formats. AdMob may
            collect and process identifiers, device information, approximate
            location, and ad interaction data to deliver and measure ads. You
            can remove ads with a Pro subscription or a one-time lifetime Pro
            purchase.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            In-App Purchases
          </h2>
          <p>
            HostPilot offers an optional Pro upgrade available as a monthly or
            annual subscription, or as a one-time lifetime purchase. Pro removes
            advertisements and unlocks additional server features. Purchases are
            processed through Google Play and are subject to Google&apos;s terms
            and privacy policies. Subscriptions can be managed or cancelled at
            any time in Google Play.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            Third-Party Services
          </h2>
          <p>HostPilot may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
          </ul>

          <h2 className="text-2xl font-bold text-emerald-400">Data Sharing</h2>
          <p>
            We do not sell your files, logs, or personal data. Data may be
            shared only with service providers required to operate app
            features, such as advertising, analytics, app distribution, or
            diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear
            the app&apos;s storage, or uninstall the app. Data collected by
            third-party services such as AdMob and Firebase is retained
            according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">
            Children&apos;s Privacy
          </h2>
          <p>
            HostPilot is not intended for children under 13, and we do not
            knowingly collect personal information from children through the
            app.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app, but
            no method of storage or transmission is completely secure. You are
            responsible for what you choose to serve and for the networks you
            serve it on.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>You decide when the server runs and what it serves.</li>
            <li>
              You can use loopback mode to keep access limited to your own
              device, or password protection (Pro) on shared networks.
            </li>
            <li>You can delete sites and clear logs from inside the app.</li>
            <li>
              You can uninstall the app to remove all locally stored data from
              your device.
            </li>
            <li>You can upgrade to Pro to disable all advertising.</li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-emerald-400">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will post the updated version on this page and revise the effective
            date.
          </p>

          <h2 className="text-2xl font-bold text-emerald-400">Contact Us</h2>
          <p>
            For privacy-related questions about HostPilot, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-cyan-400 underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-slate-800 pt-4 text-sm text-slate-500">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
